import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const UserContext = createContext();
const SESSION_MAX_AGE_MS = 8 * 60 * 60 * 1000; // 8 horas
const RAW_BASE = process.env.REACT_APP_API_URL || "";
const API_BASE = RAW_BASE.trim().replace(/\/+$/, "");

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  // Estado inicial: Valida el tiempo al cargar
  const [session, setSession] = useState(() => {
    try {
      const rawUser = sessionStorage.getItem("user");

      if (rawUser) {
        const empresa = JSON.parse(rawUser);
        const now = Date.now();

        // Si no tiene loginTime o si pasaron más de 12hs...
        if (!empresa.loginTime || (now - empresa.loginTime > SESSION_MAX_AGE_MS)) {
          sessionStorage.clear();
          return { empresa: null, perfil: null };
        }

        // Si es válido, cargamos el perfil
        const perfil = JSON.parse(sessionStorage.getItem("active_profile"));
        return { empresa, perfil };
      }
      return { empresa: null, perfil: null };
    } catch {
      return { empresa: null, perfil: null };
    }
  });

  // 1. LOGIN DE EMPRESA
  const loginEmpresa = (empresaData) => {
    const empresaId = Number(empresaData.empresa_id ?? empresaData.id);
    const dataWithTime = {
      ...empresaData,
      id: empresaId,
      empresa_id: empresaId,
      loginTime: new Date().getTime(),
    };
    sessionStorage.setItem("user", JSON.stringify(dataWithTime));
    sessionStorage.setItem("empresa_id", String(empresaId));
    setSession((prev) => ({ ...prev, empresa: dataWithTime }));
  };

  // 2. SELECCIONAR PERFIL (Vendedor/Admin)
  const selectProfile = (perfilData) => {
    // perfilData: { id, nombre, rol, ... }
    sessionStorage.setItem("active_profile", JSON.stringify(perfilData));
    setSession((prev) => ({ ...prev, perfil: perfilData }));
  };

  // 3. CERRAR USUARIO (Vuelve a selección de perfil)
  const closeProfile = () => {
    sessionStorage.removeItem("active_profile");
    setSession((prev) => ({ ...prev, perfil: null }));
  };

  // 4. CERRAR SESIÓN COMPLETA (Vuelve al Login)
  const logoutTotal = useCallback(() => {
    sessionStorage.clear(); // Borra user, empresa_id, active_profile, etc.
    setSession({ empresa: null, perfil: null });
  }, []);

  useEffect(() => {
    const currentEmpresa = session.empresa;
    if (!currentEmpresa) return;

    let mounted = true;
    const validateEmpresaSession = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/empresa/me`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error("Sesión de empresa inválida");

        const data = await res.json();
        const normalized = {
          ...currentEmpresa,
          id: data.id,
          empresa_id: data.id,
          nombre: data.nombre,
          usuario_empresa: data.usuario_empresa,
        };

        if (!mounted) return;
        sessionStorage.setItem("user", JSON.stringify(normalized));
        sessionStorage.setItem("empresa_id", String(data.id));
        const changed =
          currentEmpresa.id !== normalized.id ||
          currentEmpresa.empresa_id !== normalized.empresa_id ||
          currentEmpresa.nombre !== normalized.nombre ||
          currentEmpresa.usuario_empresa !== normalized.usuario_empresa;
        if (changed) {
          setSession((prev) => ({ ...prev, empresa: normalized }));
        }
      } catch {
        if (mounted) logoutTotal();
      }
    };

    validateEmpresaSession();

    return () => {
      mounted = false;
    };
  }, [session.empresa, logoutTotal]);

  useEffect(() => {
    // Si no hay empresa logueada, no tiene sentido vigilar
    if (!session.empresa) return;

    const intervalId = setInterval(() => {
      const rawUser = sessionStorage.getItem("user");
      
      if (rawUser) {
        const u = JSON.parse(rawUser);
        const now = Date.now();
        
        // Calcular tiempo restante
        const elapsed = now - u.loginTime;
        
        // Si pasó el tiempo límite...
        if (elapsed > SESSION_MAX_AGE_MS) {
          logoutTotal();
        }
      } else {
        logoutTotal();
      }
    }, 300000); // Chequear cada 5 minutos (ajustable)

    // Limpiar intervalo al desmontar o al desloguearse
    return () => clearInterval(intervalId);
  }, [session.empresa, logoutTotal]); // Se reinicia si cambia la empresa (login/logout)

  // Computed properties para facilitar el uso
  const isAuthenticated = !!session.empresa;
  const isProfileSelected = !!session.perfil;
  const userRole = session.perfil?.rol || null;

  return (
    <UserContext.Provider
      value={{
        empresa: session.empresa,
        perfil: session.perfil,
        isAuthenticated,
        isProfileSelected,
        userRole,
        loginEmpresa,
        selectProfile,
        closeProfile,
        logoutTotal,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
